import { useEffect, useState } from "react";
import { FileText, Download, Receipt, DownloadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getContractsByReservation, downloadContractPDF } from "@/services/contractService";
import { getInvoicesByReservation, getInvoicePDFUrl, type Invoice } from "@/services/invoiceService";
import type { Contract } from "@/types/contracts";
import { toast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const downloadFromUrl = async (url: string, filename: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

interface Props {
  reservationId: string;
}

const ReservationDocuments: React.FC<Props> = ({ reservationId }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [latestInvoice, setLatestInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, inv] = await Promise.all([
          getContractsByReservation(reservationId),
          getInvoicesByReservation(reservationId),
        ]);
        setContracts(c.filter((x) => x.status === "signed" && x.pdf_path));
        const active = inv.filter((i) => !i.archived_at && i.invoice_pdf_path);
        setLatestInvoice(active[0] || null);
      } catch (e) {
        console.error("Erreur chargement documents:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reservationId]);

  const handleContractDownload = async (contract: Contract) => {
    if (!contract.pdf_path) return;
    setDownloading(contract.id);
    try {
      await downloadContractPDF(contract.pdf_path);
    } catch {
      toast({ title: "Erreur", description: "Impossible de télécharger le contrat.", variant: "destructive" });
    } finally {
      setDownloading(null);
    }
  };

  const handleInvoiceDownload = async (invoice: Invoice) => {
    if (!invoice.invoice_pdf_path) return;
    setDownloading(invoice.id);
    try {
      const url = await getInvoicePDFUrl(invoice.invoice_pdf_path);
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `facture-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast({ title: "Erreur", description: "Impossible de télécharger la facture.", variant: "destructive" });
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading("all");
    try {
      const tasks: Promise<void>[] = [];
      if (latestInvoice?.invoice_pdf_path) {
        tasks.push(
          (async () => {
            const url = await getInvoicePDFUrl(latestInvoice.invoice_pdf_path!);
            await downloadFromUrl(url, `facture-${latestInvoice.invoice_number}.pdf`);
          })()
        );
      }
      contracts.forEach((c) => {
        if (c.pdf_path) {
          const url = `https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/generated-contracts/${c.pdf_path}`;
          const filename = c.pdf_path.split("/").pop() || `contrat-${c.contract_type}.pdf`;
          tasks.push(downloadFromUrl(url, filename));
        }
      });
      await Promise.all(tasks);
      toast({ title: "Téléchargement lancé", description: "Toutes les pièces ont été téléchargées." });
    } catch (e) {
      console.error(e);
      toast({ title: "Erreur", description: "Impossible de télécharger toutes les pièces.", variant: "destructive" });
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return null;
  if (!latestInvoice && contracts.length === 0) return null;

  const totalDocs = (latestInvoice ? 1 : 0) + contracts.length;

  const contractLabel = (type: string) =>
    type === "lease" ? "Contrat de location" : type === "rules" ? "Règlement intérieur" : "Document";

  return (
    <>
      <Separator className="my-6" />
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-villa-blue">Mes documents</h4>
          {totalDocs > 1 && (
            <Button
              size="sm"
              onClick={handleDownloadAll}
              disabled={downloading === "all"}
            >
              <DownloadCloud className="h-4 w-4 mr-1" />
              Tout télécharger
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {latestInvoice && (
            <div className="flex items-center justify-between p-3 bg-white rounded border">
              <div className="flex items-center space-x-3">
                <Receipt className="h-5 w-5 text-villa-turquoise" />
                <div>
                  <p className="text-sm font-medium">Facture {latestInvoice.invoice_number}</p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(latestInvoice.created_at), "d MMMM yyyy", { locale: fr })}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleInvoiceDownload(latestInvoice)}
                disabled={downloading === latestInvoice.id}
              >
                <Download className="h-4 w-4 mr-1" />
                PDF
              </Button>
            </div>
          )}
          {contracts.map((c) => (
            <div key={c.id} className="flex items-center justify-between p-3 bg-white rounded border">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-villa-turquoise" />
                <div>
                  <p className="text-sm font-medium">{contractLabel(c.contract_type)}</p>
                  {c.signed_at && (
                    <p className="text-xs text-gray-500">
                      Signé le {format(new Date(c.signed_at), "d MMMM yyyy", { locale: fr })}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleContractDownload(c)}
                disabled={downloading === c.id || !c.pdf_path}
              >
                <Download className="h-4 w-4 mr-1" />
                PDF
              </Button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ReservationDocuments;
